class Questionnaire {

    constructor(element, {
        notRequired = 'optioneel',
        answerRequired = 'Maak een keuze uit de antwoorden',
        vraagProgress = 'Vraag {1}',
        requireAnswer = false,
        questionUrl = '/api/sitecore/vraagantwoordvragenlijst/getquestionandanswers',
        adviceUrl = '/api/sitecore/vraagantwoordvragenlijst/displayadvice'
    } = {}) {
        if (!element) return;

        this._element = element;
        this._options = {
            notRequired,
            answerRequired,
            vraagProgress,
            requireAnswer,
            questionUrl,
            adviceUrl
        };

        this.s = {
            questionFolderId: '[data-questionnaire-folder]',
            huidigeVraagId: '[data-questionnaire-huidige-vraag]',
            submitButton: '[data-questionnaire-submit]',
            backButton: '[data-questionnaire-backbutton]',
            answersContainer: '[data-questionnaire-answer-container]',
            question: '[data-questionnaire-question]',
            questionHeading: '[data-questionnaire-heading]',
            questionInfo: '[data-questionnaire-info]',
            questionContent: '[data-questionnaire-content]',
            questionDescription: '[data-questionnaire-description]',
            questionMessage: '[data-questionnaire-message]',
            bar: '[data-questionnaire-bar]',
            indicator: '[data-questionnaire-indicator]',

            summaryContainer:'[data-questionnaire-summary]',
            summaryLabel:'[data-questionnaire-summary-label]',
            summaryItems:'[data-questionnaire-summary-items]',
            summaryHerstartLink: '[data-questionnaire-summary-herstartlink]',
            summaryHerstartLinkLabel: '[data-questionnaire-summary-herstartlinklabel]'

        };

        this.answers = {};

        this.init();
    }

    init() {
        this.questionFolderId = this._element.querySelector(this.s.questionFolderId)?.value || "";
        this.huidigeVraagId = this._element.querySelector(this.s.huidigeVraagId)?.value || "";
        this.submitButton = this._element.querySelector(this.s.submitButton);
        this.backButton = this._element.querySelector(this.s.backButton);
        this.answersContainer = this._element.querySelector(this.s.answersContainer);

        this.initEvents();

        this.question = this._element.querySelector(this.s.question);
        this.questionHeading = this._element.querySelector(this.s.questionHeading);
        this.questionInfo = this._element.querySelector(this.s.questionInfo);
        this.questionContent = this._element.querySelector(this.s.questionContent);
        this.questionDescription = this._element.querySelector(this.s.questionDescription);
        this.questionMessage = this._element.querySelector(this.s.questionMessage);
        this.bar = this._element.querySelector(this.s.bar);
        this.indicator = this._element.querySelector(this.s.indicator);

        this.summaryContainer = this._element.querySelector(this.s.summaryContainer);
        this.summaryLabel = this._element.querySelector(this.s.summaryLabel);
        this.summaryItems = this._element.querySelector(this.s.summaryItems);
        this.summaryHerstartLink = this._element.querySelector(this.s.summaryHerstartLink);
        this.summaryHerstartLinkLabel = this._element.querySelector(this.s.summaryHerstartLinkLabel);

        let vraagActie = 0; // volgende vraag ophalen
        if (this.huidigeVraagId !== "") {
            vraagActie = 2; //huidige vraag ophalen
        }

        this.connectForQuestion({ 
            vragenlijstFolder: this.questionFolderId,
            huidigeVraagId: this.huidigeVraagId,
            nextQuestion: vraagActie
        });
    }

    initEvents() {

        this.submitButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.onSubmitHandler(e);
        });

        this.backButton.addEventListener('click', (e) => {
            this.onBackHandler(e);
        });

        this.answersContainer.addEventListener('click', (e) => {
            this.onAnswerHandler(e);
        });
    }

    onSubmitHandler(e) {
        // See if there are answers
        this.collectAnswers();

        if (this._options.requireAnswer && this.answers.length <= 0) {
            this.showError(this._options.answerRequired);
            return false;
        }

        this.scrollToTitle();

        this.connectForQuestion({
            vragenlijstFolder: this.questionFolderId,
            huidigeVraagId: this.huidigeVraagId,
            huidigeVraagAntwoordId: this.answers,
            nextQuestion: 0
        });
    }
    
    onBackHandler(e) {
        this.scrollToTitle();
        
        this.connectForQuestion({
            vragenlijstFolder: this.questionFolderId,
            huidigeVraagId: this.huidigeVraagId,
            nextQuestion: 1
        });
    }

    onAnswerHandler(e) {
        this.highlightAnswers();
        this.collectAnswers();
    }

    async connectForQuestion(data) {

        try {
            const fetchPromise = fetch(this._options.questionUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data)
            });
            const response = await fetchPromise;
            if (!response.ok) {
                console.log("Er is een fout opgetreden in de connectie met de server.");
            }
            const result = await response.json();
            this.renderQuestion(result);
            this.renderSummary(result);
            this._options.answerRequired = result.VraagFoutmelding;            
        } catch (error) {
            console.log("Er is een fout opgetreden in de connectie met de server.");
        }
    }

    collectAnswers() {
        this.answers = [].map.call(this._element.querySelectorAll('input:checked,input[type="text"]'),
            function (obj) {
                let inputType = obj.getAttribute('type');
                let inputId = obj.getAttribute('id');
                let value = "";

                // Return the value. for `text`, `radio` or `checkbox`
                if (inputType === 'text' && obj.value.length > 0) {
                    // Combine the id and value in a string as the
                    // backend isn't accepting objects or arrays as an
                    // answer.
                    //
                    // Remove this comment and the `.join` when the
                    // backend accepts this format.
                    value = [inputId, obj.value].join(':');
                } else {
                    value = inputId;
                }

                return value;
            });
    }

    renderQuestion(question) {
        // Clean before creation
        this.removeAnswers();
        this.removeErrors();

        // Show content, hide question form
        this.toggleQuestionContent('question');

        // Apply button management
        this.toggleButtons(question.VraagNummer);

        // General question
        this.huidigeVraagId = question.Vraag.Id || "";
        this.questionHeading.innerHTML = question.Vraag.Vraag;

        this.questionDescription.innerHTML = question.Vraag.VraagOmschrijving;
        this.questionMessage.innerHTML = question.Vraag.VraagToelichting;

        // Is the question optional?
        this._options.requireAnswer = question.Vraag.AntwoordVerplicht;
        if (this._options.requireAnswer === false) {
            this.questionHeading.innerHTML += '<span class="sub-label"> ' + this._options.notRequired + '</span>';
        }

        // Show the progress
        let percentage = question.PercentageAfgerondeVragen + "%";
        this.bar.style.width = percentage;

        // Show the numbers
        let indicator = this._options.vraagProgress;
        indicator = indicator.replace("{1}", question.VraagNummer);
        indicator = indicator.replace("{2}", question.TotaalAantalVragen);
        this.indicator.innerHTML = indicator || "";
        // Create and format the answers
        if (question.Antwoorden && question.Antwoorden.length > 0) {
            //add two columns classes when more than 11 answers
            let containerElement = document.createElement('div');
            containerElement.setAttribute('aria-labelledby', this.questionHeading.id);

            let containerClassName = 'c-input__container';
            let containerRole = 'group';

            // Fill the answer if available
            switch (question.Vraag.VraagType) {
                case 'radio':
                    containerClassName = 'c-input-radio';
                    containerRole = 'radiogroup';
                    break;
                case 'checkbox':
                    containerClassName = 'c-input-checkbox';
                    break;

                case 'text':
                    containerClassName = 'c-input-text';
                    break;

                default:
            }

            containerElement.classList.add(containerClassName);
            containerElement.role = containerRole;
            
            for (const answer of question.Antwoorden) {

                let inputOption = document.createElement('div');
                inputOption.classList.add(`${containerClassName}__item`);

                let labelElement = document.createElement('label');
                labelElement.setAttribute('for', answer.Id);
                labelElement.classList.add('c-label');

                // input types are Checkbox, Radio or Text
                let inputElement = document.createElement('input');
                inputElement.classList.add('c-input');
                inputElement.setAttribute('name', question.Vraag.Id);
                inputElement.setAttribute('type', question.Vraag.VraagType);
                inputElement.setAttribute('id', answer.Id);
                if (question.Vraag.AntwoordVerplicht) {
                    inputElement.setAttribute('required', 'required');
                }
                inputElement.addEventListener('input', () => {
                    this.removeErrors();
                });


                // Fill the answer if available
                switch (question.Vraag.VraagType) {
                    case 'checkbox':
                    case 'radio':
                        if (answer.GegevenAntwoord === true) {
                            inputElement.setAttribute('checked', 'checked');
                        }
                        break;

                    case 'text':
                        inputElement.setAttribute('value', answer.GegevenAntwoordTekst);
                        break;

                    default:

                }

                labelElement.appendChild(inputElement);
                let spanElement = document.createElement('span');
                spanElement.classList.add(`${containerClassName}__label-text`);
                spanElement.innerHTML = answer.Antwoord;
                labelElement.appendChild(spanElement);
                inputOption.appendChild(labelElement);
                containerElement.appendChild(inputOption);

                this.highlightAnswers();
            }
            this.answersContainer.appendChild(containerElement);
        }

        window.processModulesInsideElement(this._element);
    }

    renderSummary(data) {

        this.summaryContainer.classList.add("t-hidden");

        if (data.Samenvatting) {

            if (data.Samenvatting.GegevenAntwoordItems.length !== 0) {
                this.summaryContainer.classList.remove('t-hidden');
            }

            //render summary label
            this.summaryLabel.innerHTML = data.Samenvatting.SamenvattingLayout.Label;

             //render summary items in <ol>
            //clear <ol>
            this.summaryItems.innerHTML = '';

            for (const item of data.Samenvatting.GegevenAntwoordItems) {
                const liElement = document.createElement('li');

                const linkElement = document.createElement('a');
                const linkText = document.createElement('span');
                linkText.innerHTML = item.SamenvattingWijzigLinkLabel;
                linkText.classList.add('c-link__text');
                linkElement.appendChild(linkText);
                linkElement.title = item.SamenvattingWijzigLinkLabel;
                linkElement.href = "#";
                const id = item.VraagId;
                linkElement.setAttribute('id', id);

                linkElement.classList.add('c-link');
                linkElement.addEventListener('click', () => {
                    this.huidigeVraagId = id;
                    this.connectForQuestion({
                        vragenlijstFolder: this.questionFolderId,
                        huidigeVraagId: id,
                        nextQuestion: 2
                    });
                });

                liElement.innerHTML = item.AntwoordTekstVoorSamenvatting + ' ';
                liElement.appendChild(linkElement);
                this.summaryItems.appendChild(liElement);
            }

            //render herstartlink
            this.summaryHerstartLink.href = data.Samenvatting.SamenvattingLayout.HerstartLink;
            this.summaryHerstartLinkLabel.innerHTML = data.Samenvatting.SamenvattingLayout.HerstartLinkLabel;

        }

    }

    removeAnswers() {
        this.removeBySelector('[role="radiogroup"],[role="group"]');
    }

    removeBySelector(label) {
        let items = this._element.querySelectorAll(label);

        // Remove the elements
        for (const item of items) {
            if (item?.parentNode) {
                item.parentNode.removeChild(item);
            }
        }
    }


    showError(message) {
        let errorMessage = document.createElement('div');
        errorMessage.className = 'c-form-field__error active';
        errorMessage.innerHTML = message;
        errorMessage.style.display = 'block';

        this.removeErrors();

        this.answersContainer.appendChild(errorMessage);


        return false;
    }

    removeErrors() {
        this.removeBySelector('.c-form-field__error');
    }

   toggleQuestionContent(section) {
        let question,
            content;

        if (section === 'question') {
            question = '';
            content = 'none';
        } else {
            question = 'none';
            content = '';
        }
        this.questionInfo.style.display = question;
        this.question.style.display = question;
        this.questionContent.style.display = content;
    }

    toggleButtons(progress) {
        if (progress > 1) {
            this.backButton.classList.remove('t-hidden');
        } else {
            this.backButton.classList.add('t-hidden')
        }
    }

    scrollToTitle() {
        return;
    }

    highlightAnswers() {
        const labels =  this.answersContainer.querySelectorAll('label');

        for (let i = 0, l = labels.length; i < l; i++) {
            const item = labels[i];

            if (item.querySelector('input:checked')) {
                item.classList.add('selected');
            } else {
                item.classList.remove('selected');
            }
        }
    }
}

export default Questionnaire;
